import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as authApi from '@/api/auth'
import { clearAccessToken, getAccessToken } from '@/api/client'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [siteRequireMfa, setSiteRequireMfa] = useState(true)

  const loadUser = useCallback(async () => {
    const data = await authApi.fetchCurrentUser()
    setUser(data)
    return data
  }, [])

  const bootstrap = useCallback(async () => {
    setLoading(true)
    try {
      try {
        const res = await fetch('/api/health')
        if (res.ok) {
          const health = await res.json()
          if (typeof health.require_mfa === 'boolean') {
            setSiteRequireMfa(health.require_mfa)
          }
        }
      } catch {
        // Keep default if health is unreachable
      }

      const token = getAccessToken()
      if (token) {
        await loadUser()
      } else {
        try {
          await authApi.refreshSession()
          await loadUser()
        } catch {
          setUser(null)
        }
      }
    } catch {
      clearAccessToken()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [loadUser])

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  useEffect(() => {
    const onUnauthorized = () => {
      clearAccessToken()
      setUser(null)
      setLoading(false)
    }
    window.addEventListener('bekat-kb:unauthorized', onUnauthorized)
    return () => window.removeEventListener('bekat-kb:unauthorized', onUnauthorized)
  }, [])

  const login = useCallback(
    async (email, password, totpCode, rememberMfa = false) => {
      await authApi.login(email, password, totpCode, rememberMfa)
      return loadUser()
    },
    [loadUser],
  )

  const logout = useCallback(async () => {
    await authApi.logout()
    clearAccessToken()
    setUser(null)
  }, [])

  // Logged-in users: per-user policy (admin site flag or linked customers).
  // Pre-login: site admin default from health (enrolment only applies after login).
  const requireMfa =
    user && typeof user.require_mfa === 'boolean'
      ? user.require_mfa
      : siteRequireMfa

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshUser: loadUser,
      setUser,
      isAdmin: user?.role === 'admin',
      isCustomer: user?.role === 'customer',
      canEdit: Boolean(user?.effective_can_edit),
      canViewSecrets: Boolean(user?.effective_can_view_secrets),
      canUseAiSearch: Boolean(user?.effective_can_use_ai_search),
      mfaEnabled: Boolean(user?.totp_enabled),
      requireMfa,
    }),
    [user, loading, login, logout, loadUser, requireMfa],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
